import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Tab,
  Tabs,
  LinearProgress,
  FormControl,
} from "@mui/material";
import {
  Activity,
  AlertCircle,
  Briefcase,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  Copy,
  Database,
  Key,
  RefreshCw,
  Settings as SettingsIcon,
  TrendingDown,
  TrendingUp,
  Award,
  BookOpen,
  Calendar,
  Clock,
  Target,
  Search,
} from "lucide-react";
import {
  CartesianGrid,
  Tooltip as ChartTooltip,
  Line,
  LineChart,
  PolarAngleAxis,
  PolarGrid,
  PolarRadiusAxis,
  Radar,
  RadarChart,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from "recharts";
import { toast } from "react-toastify";
import apiClient from "@/api/apiClient";
import { useUserStore } from "@/store";
import useAuthStore from "@/store/useAuthStore";
import { canViewCRMSettings } from '@/config/permissions';

interface SyncLog {
  id: string;
  tenantId: string;
  entityType: string;
  entityId: string;
  status: string;
  retryCount: number;
  error?: string;
  createdAt: string;
}

interface ImportJob {
  id: string;
  status: "pending" | "running" | "completed" | "failed" | "retrying";
  totalContacts: number;
  processedContacts: number;
  failedContacts: number;
  error?: string;
}

interface Customer {
  id: string;
  companyName: string;
  industry?: string;
}

import { useDashboardTheme } from "../MeetingDashboard/themeHelper";
import { MicrosoftCalendarIntegration } from "./MicrosoftCalendarIntegration";

const Settings: React.FC = () => {
  const { isDark, colors } = useDashboardTheme();
  const { id: userId, role } = useAuthStore();
  const { setPageHeader } = useUserStore();

  const isManagerOrAdmin = ["MANAGER", "ADMIN", "SUPER_ADMIN"].includes(role);
  const isCompanyAdmin = ["MANAGER", "ADMIN"].includes(role) && role !== "SUPER_ADMIN";

  const tabsConfig = [
    { label: "Deal Timeline & Performance", visible: true, originalIndex: 0 },
    { label: "CRM Setup & Sync", visible: canViewCRMSettings(role as string), originalIndex: 1 },
    { label: "Microsoft 365 & Calendar", visible: isCompanyAdmin, originalIndex: 2 }
  ];
  const visibleTabs = tabsConfig.filter(t => t.visible);

  // Tab State - Auto-select calendar tab if ?tab=calendar is in URL
  const [activeTab, setActiveTab] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("tab") === "calendar" && isCompanyAdmin) {
      const calIdx = visibleTabs.findIndex(t => t.originalIndex === 2);
      return calIdx >= 0 ? calIdx : 0;
    }
    return 0;
  });

  // Keep active tab in sync if role loads asynchronously or redirect arrives with ?tab=calendar
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("tab") === "calendar" && isCompanyAdmin) {
      const calIdx = visibleTabs.findIndex(t => t.originalIndex === 2);
      if (calIdx >= 0 && activeTab !== calIdx) {
        setActiveTab(calIdx);
      }
    }
  }, [isCompanyAdmin, visibleTabs, activeTab]);

  const activeOriginalIndex = visibleTabs[activeTab]?.originalIndex ?? 0;

  // VIEW 1: Deal Timeline Sub-tab state
  const [timelineTab, setTimelineTab] = useState(0);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState<string>("");
  const [timelineData, setTimelineData] = useState<any>(null);
  const [timelineLoading, setTimelineLoading] = useState<boolean>(false);
  const [selectedActivityId, setSelectedActivityId] = useState<string | null>(null);
  const [isEvaluating, setIsEvaluating] = useState(false);

  // VIEW 2: Rep Progress State
  const [repPeriod, setRepPeriod] = useState<string>("30d");
  const [repData, setRepData] = useState<any>(null);
  const [repLoading, setRepLoading] = useState<boolean>(false);

  // VIEW 3: Team Progress State
  const [teamPeriod, setTeamPeriod] = useState<string>("30d");
  const [teamData, setTeamData] = useState<any[]>([]);
  const [teamLoading, setTeamLoading] = useState<boolean>(false);

  // Tab 2: Setup & Sync State
  const [logs, setLogs] = useState<SyncLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [connected, setConnected] = useState(false);
  const [importJob, setImportJob] = useState<ImportJob | null>(null);
  const [pollingActive, setPollingActive] = useState(false);

  // Setup credentials form
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [appType, setAppType] = useState<"PUBLIC" | "PRIVATE">("PUBLIC");
  const [instructionsOpen, setInstructionsOpen] = useState(false);

  // Setup config states
  const [evaluateHistorical, setEvaluateHistorical] = useState(false);
  const [historicalDaysLimit, setHistoricalDaysLimit] = useState(30);
  const [enableDealLinking, setEnableDealLinking] = useState(false);
  const [enableDealCreation, setEnableDealCreation] = useState(false);

  // Search & filter logs state
  const [searchTenant, setSearchTenant] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [typeFilter, setTypeFilter] = useState("ALL");

  useEffect(() => {
    setPageHeader("CRM Operations Oversight");
    loadCustomers();
    loadCRMConfig();
    loadSyncLogs();
  }, [setPageHeader]);

  // Auto-switch to tab if specified in URL query (e.g. ?tab=calendar)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tabParam = params.get("tab");
    if (tabParam === "calendar" || tabParam === "microsoft" || tabParam === "integrations") {
      const idx = visibleTabs.findIndex(t => t.originalIndex === 2);
      if (idx !== -1) setActiveTab(idx);
    }
  }, [visibleTabs]);

  // Load Customer Account List
  const loadCustomers = async () => {
    try {
      const res = await apiClient.get("/v1/customer");
      if (res.data.status && res.data.data?.items) {
        setCustomers(res.data.data.items);
        if (res.data.data.items.length > 0) {
          setSelectedCustomerId(res.data.data.items[0].id);
        }
      }
    } catch (err) {
      console.error("Failed to load customer list:", err);
    }
  };

  // Load CRM Configuration
  const loadCRMConfig = useCallback(async () => {
    try {
      setLoading(true);
      const res = await apiClient.get("v1/crm/config");
      if (res.data.status && res.data.data) {
        const config = res.data.data;
        setConnected(config.isActive);

        if (config.crmType === "HUBSPOT") {
          setClientId(config.credentials?.clientId || "");
          setClientSecret(config.credentials?.clientSecret || "");
        }

        if (config.fieldMappings?.evaluationConfig) {
          setEvaluateHistorical(config.fieldMappings.evaluationConfig.evaluateHistorical ?? false);
          setHistoricalDaysLimit(config.fieldMappings.evaluationConfig.historicalDaysLimit ?? 30);
        }

        if (config.fieldMappings?.dealLinkingConfig) {
          setEnableDealLinking(config.fieldMappings.dealLinkingConfig.enableDealLinking ?? false);
          setEnableDealCreation(config.fieldMappings.dealLinkingConfig.enableDealCreation ?? false);
        }
      }
    } catch (err) {
      console.error("Failed to load CRM config:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load Synchronisation Logs
  const loadSyncLogs = useCallback(async () => {
    try {
      const res = await apiClient.get("v1/crm/logs");
      if (res.data.status && res.data.data) {
        setLogs(res.data.data);
      }
    } catch (err) {
      console.error("Failed to load crm logs:", err);
    }
  }, []);

  // Check Bulk Import Job Status
  const checkImportStatus = useCallback(async () => {
    try {
      const res = await apiClient.get("v1/crm/import/status");
      if (res.data.status && res.data.data) {
        const job = res.data.data;
        setImportJob(job);
        if (job.status === "running" || job.status === "pending" || job.status === "retrying") {
          setSyncing(true);
          return true;
        } else {
          setSyncing(false);
          return false;
        }
      } else {
        setImportJob(null);
        setSyncing(false);
        return false;
      }
    } catch (err) {
      console.error("Failed to check import status:", err);
      return false;
    }
  }, []);

  // Polling import status if sync active
  useEffect(() => {
    let intervalId: ReturnType<typeof setInterval> | null = null;

    const startPolling = async () => {
      const isSyncing = await checkImportStatus();
      if (isSyncing) {
        intervalId = setInterval(async () => {
          const stillRunning = await checkImportStatus();
          if (!stillRunning) {
            if (intervalId) clearInterval(intervalId);
            loadSyncLogs();
            toast.success("CRM Import Job completed!");
          }
        }, 3000);
      }
    };

    startPolling();

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [checkImportStatus, loadSyncLogs, pollingActive]);

  // Save CRM Configuration Handler
  const handleSaveConfig = async () => {
    try {
      setLoading(true);
      const payload = {
        crmType: "HUBSPOT",
        credentials: { clientId, clientSecret },
        fieldMappings: {
          evaluationConfig: {
            evaluateHistorical,
            historicalDaysLimit,
          },
          dealLinkingConfig: {
            enableDealLinking,
            enableDealCreation,
          },
        },
      };

      const res = await apiClient.post("v1/crm/config", payload);
      if (res.data.status) {
        toast.success("CRM Configuration saved successfully.");
        setConnected(res.data.data.isActive);
        loadSyncLogs();
      } else {
        toast.error(res.data.message || "Failed to save configuration.");
      }
    } catch (err: any) {
      const errorMsg = err.response?.data?.message || "Error occurred while saving configuration.";
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // Initiate HubSpot OAuth Handler
  const handleTriggerOAuth = async () => {
    if (appType === "PRIVATE" && (!clientId || !clientSecret)) {
      toast.error("Client ID and Client Secret are required for Private App authorization.");
      return;
    }

    try {
      setLoading(true);
      const url = appType === "PRIVATE" 
        ? `v1/crm/oauth/connect?clientId=${encodeURIComponent(clientId)}&clientSecret=${encodeURIComponent(clientSecret)}&_t=${Date.now()}`
        : `v1/crm/oauth/connect?_t=${Date.now()}`;
      
      const res = await apiClient.get(url);
      if (res.data.success && res.data.url) {
        window.location.href = res.data.url;
      } else {
        toast.error("Failed to get OAuth connection URL.");
      }
    } catch (err: any) {
      const errorMsg = err.response?.data?.message || "Error initiating OAuth handshake.";
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // Trigger Bulk Import Handler
  const handleImportCRMData = async () => {
    try {
      setSyncing(true);
      const res = await apiClient.post("v1/crm/import");
      if (res.data.status) {
        toast.success(
          "Bulk CRM import queued. Synthesizing AI digital twin personas in background."
        );
        setPollingActive((p) => !p);
      } else {
        toast.error("Failed to trigger bulk import sync.");
        setSyncing(false);
      }
    } catch (err: any) {
      const errorMsg = err.response?.data?.message || "Bulk import request failed.";
      toast.error(errorMsg);
      setSyncing(false);
    }
  };

  // Cancel Import Job Handler
  const handleCancelImport = async () => {
    try {
      await apiClient.post("v1/crm/import/cancel");
      toast.success("Sync cancelled successfully.");
      checkImportStatus();
    } catch {
      toast.error("Failed to cancel sync.");
    }
  };

  // Fetch Timeline Data
  const fetchTimeline = async () => {
    if (!selectedCustomerId) return;
    setTimelineLoading(true);
    try {
      const response = await apiClient.get("v1/progress/timeline", {
        params: { customerId: selectedCustomerId },
      });
      setTimelineData(response.data);
      if (response.data?.activities?.length > 0) {
        setSelectedActivityId(response.data.activities[response.data.activities.length - 1].id);
      } else {
        setSelectedActivityId(null);
      }
    } catch (err) {
      console.error("Error fetching timeline:", err);
    } finally {
      setTimelineLoading(false);
    }
  };

  // Fetch Rep Progress Dashboard Data
  const fetchRepProgress = async () => {
    setRepLoading(true);
    try {
      const response = await apiClient.get(`v1/progress/rep/${userId}`, {
        params: { period: repPeriod },
      });
      setRepData(response.data);
    } catch (err) {
      console.error("Error fetching rep progress:", err);
    } finally {
      setRepLoading(false);
    }
  };

  // Fetch Team Progress Dashboard Data
  const fetchTeamProgress = async () => {
    setTeamLoading(true);
    try {
      const response = await apiClient.get("v1/progress/team", {
        params: { period: teamPeriod },
      });
      setTeamData(response.data);
    } catch (err) {
      console.error("Error fetching team progress:", err);
    } finally {
      setTeamLoading(false);
    }
  };

  // Effect triggers for Deal Timeline tab
  useEffect(() => {
    if (activeOriginalIndex === 0 && timelineTab === 0 && selectedCustomerId) {
      fetchTimeline();
    }
  }, [selectedCustomerId, activeOriginalIndex, timelineTab]);

  useEffect(() => {
    if (activeOriginalIndex === 0 && timelineTab === 1) {
      fetchRepProgress();
    }
  }, [repPeriod, activeOriginalIndex, timelineTab]);

  useEffect(() => {
    if (activeOriginalIndex === 0 && timelineTab === 2 && isManagerOrAdmin) {
      fetchTeamProgress();
    }
  }, [teamPeriod, activeOriginalIndex, timelineTab]);

  // Trigger AI evaluation manually
  const handleEvaluate = async () => {
    if (!selectedActivity || (!selectedActivity.meetingId && !selectedActivity.pitchId)) return;
    setIsEvaluating(true);
    try {
      const type = selectedActivity.type === "role_play" ? "pitch" : "meeting";
      const id =
        selectedActivity.type === "role_play"
          ? selectedActivity.pitchId
          : selectedActivity.meetingId;
      const res = await apiClient.post(`v1/crm/evaluate-engagement/${type}/${id}`);
      if (res.status === 200) {
        toast.success("AI Evaluation successfully enqueued!");
        selectedActivity.evaluationStatus = "PENDING_EVALUATION";
        fetchTimeline();
      }
    } catch (e) {
      console.error(e);
      toast.error("Failed to trigger evaluation.");
    } finally {
      setIsEvaluating(false);
    }
  };

  const selectedActivity = timelineData?.activities?.find((a: any) => a.id === selectedActivityId);

  // Format date utility
  const formatDate = (dateStr: string) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  // Dimensional Mappings
  const dimensionKeys = [
    { key: "discovery_effectiveness", name: "Discovery" },
    { key: "value_communication", name: "Value Comm." },
    { key: "buyer_engagement", name: "Buyer Eng." },
    { key: "objection_handling", name: "Objection" },
    { key: "deal_control", name: "Deal Control" },
    { key: "communication_presence", name: "Presence" },
  ];

  // Prepare radar chart data
  const getRadarData = () => {
    if (!timelineData?.activities || timelineData.activities.length === 0) return [];
    const validActivities = timelineData.activities.filter(
      (a: any) =>
        a.evaluationStatus !== "MANUAL_EVALUATION_REQUIRED" &&
        a.evaluationStatus !== "PENDING_EVALUATION"
    );
    if (validActivities.length === 0) return [];
    const firstAct = validActivities[0];
    const latestAct = validActivities[validActivities.length - 1];

    return dimensionKeys.map((dim) => ({
      subject: dim.name,
      First: firstAct.scores?.[dim.key] || 0,
      Latest: latestAct.scores?.[dim.key] || 0,
      fullMark: 100,
    }));
  };

  // Prepare line chart data
  const getLineData = () => {
    if (!timelineData?.activities) return [];
    return timelineData.activities
      .filter(
        (a: any) =>
          a.evaluationStatus !== "MANUAL_EVALUATION_REQUIRED" &&
          a.evaluationStatus !== "PENDING_EVALUATION"
      )
      .map((a: any) => ({
        name: `${a.type === "role_play" ? "Prep" : "Call"} #${a.sequenceNumber}`,
        Score: a.overallScore,
      }));
  };

  // Filter logs list
  const filteredLogs = logs.filter((log) => {
    const matchesTenant =
      log.tenantId.toLowerCase().includes(searchTenant.toLowerCase()) ||
      log.entityId.toLowerCase().includes(searchTenant.toLowerCase());
    const matchesStatus = statusFilter === "ALL" || log.status === statusFilter;
    const matchesType = typeFilter === "ALL" || log.entityType === typeFilter;
    return matchesTenant && matchesStatus && matchesType;
  });

  return (
    <Box className={"p-6 mt-20 font-poppins flex flex-col gap-6 " + (isDark ? "text-white" : "light-theme-dashboard")} style={{ background: isDark ? "" : "#F4F5F7" }}>
      {/* Top Tabs */}
      <Box className="flex justify-between items-center mb-2">
        <Tabs
          value={activeTab}
          onChange={(_, val) => {
            setActiveTab(val);
            const selectedTab = visibleTabs[val];
            const url = new URL(window.location.href);
            if (selectedTab?.originalIndex === 2) {
              url.searchParams.set("tab", "calendar");
            } else {
              url.searchParams.delete("tab");
            }
            window.history.replaceState({}, document.title, url.pathname + url.search);
          }}
          sx={{
            "& .MuiTabs-indicator": { backgroundColor: "#a855f7" },
            "& .MuiTab-root": { color: "rgba(255, 255, 255, 0.8)", fontSize: "0.875rem" },
            "& .Mui-selected": { color: "#a855f7 !important", fontWeight: "bold" },
          }}
        >
          {visibleTabs.map((tab, idx) => (
            <Tab key={idx} label={tab.label} />
          ))}
        </Tabs>

        {activeOriginalIndex === 1 && (
          <button
            onClick={loadSyncLogs}
            className="flex items-center gap-1.5 px-4 py-2 bg-[#DBDBDB1A] hover:bg-white/10 font-semibold poppins text-white rounded-full transition cursor-pointer"
          >
            <RefreshCw className="w-3.5 h-3.5" /> Reload sync logs
          </button>
        )}
      </Box>

      {/* TAB 0: DEAL TIMELINE & PERFORMANCE */}
      {activeOriginalIndex === 0 && (
        <Box className="space-y-6">
          <Box className="flex flex-col md:flex-row md:items-center md:justify-between">
            <Box>
              <h2 className="text-xl font-bold text-white">Practice & Performance Analytics</h2>
              <p className="text-white/70 mt-1 text-xs">
                Review preparation insights, actual sales calls, and growth dynamics across CRM-linked deals.
              </p>
            </Box>

            <Box className="mt-4 md:mt-0">
              <Tabs
                value={timelineTab}
                onChange={(_, val) => setTimelineTab(val)}
                sx={{
                  "& .MuiTabs-indicator": { backgroundColor: "#a855f7" },
                  "& .MuiTab-root": { color: "rgba(255, 255, 255, 0.8)", fontSize: "0.8rem" },
                  "& .Mui-selected": { color: "#a855f7 !important", fontWeight: "bold" },
                }}
              >
                <Tab label="Deal Timeline" />
                <Tab label="Rep Dashboard" />
                {isManagerOrAdmin && <Tab label="Team Performance" />}
              </Tabs>
            </Box>
          </Box>

          {/* VIEW 0.0: DEAL TIMELINE NODES AND METRICS */}
          {timelineTab === 0 && (
            <Box className="space-y-6">
              {/* Customer Selector & Metrics Header */}
              <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 items-stretch">
                {/* Selector */}
                <div className="bg-[#1C1A26] border border-[#313131] rounded-2xl p-4 flex items-center justify-between h-full">
                  <div className="w-full">
                    <Typography component="div" className="text-xs text-[var(--color-muted)] mb-2 font-medium">
                      Select Account
                    </Typography>
                    <FormControl fullWidth variant="outlined" size="small">
                      <Select
                        value={selectedCustomerId}
                        onChange={(e) => setSelectedCustomerId(e.target.value as string)}
                        sx={{
                          color: "var(--color-text)",
                          ".MuiOutlinedInput-notchedOutline": { borderColor: "var(--color-border)" },
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#a855f7" },
                          ".MuiSelect-icon": { color: "var(--color-text)" },
                          backgroundColor: "var(--color-bg)",
                        }}
                        MenuProps={{
                          PaperProps: {
                            sx: {
                              backgroundColor: "var(--color-surface)",
                              color: "var(--color-text)",
                              border: "1px solid var(--color-border)",
                            }
                          }
                        }}
                      >
                        {customers.map((c) => (
                          <MenuItem
                            key={c.id}
                            value={c.id}
                            sx={{
                              color: "var(--color-text)",
                              "&:hover": {
                                backgroundColor: "var(--color-divider)",
                              },
                            }}
                          >
                            {c.companyName}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </div>
                </div>

                {/* Metrics */}
                {timelineData && (
                  <div className="lg:col-span-3 grid grid-cols-3 gap-4">
                    <Card sx={{ backgroundColor: "#1C1A26", height: "100%" }} className="border border-[#313131] text-white rounded-2xl">
                      <CardContent className="p-4 flex flex-col items-center justify-center">
                        <Typography className="text-xs text-white/80 font-medium">Prep Average</Typography>
                        <Typography className="text-xl font-bold text-purple-400 mt-1">
                          {timelineData.prepAverage}/100
                        </Typography>
                      </CardContent>
                    </Card>

                    <Card sx={{ backgroundColor: "#1C1A26", height: "100%" }} className="border border-[#313131] text-white rounded-2xl">
                      <CardContent className="p-4 flex flex-col items-center justify-center">
                        <Typography className="text-xs text-white/80 font-medium">Meeting Average</Typography>
                        <Typography className="text-xl font-bold text-blue-400 mt-1">
                          {timelineData.meetingAverage}/100
                        </Typography>
                      </CardContent>
                    </Card>

                    <Card sx={{ backgroundColor: "#1C1A26", height: "100%" }} className="border border-[#313131] text-white rounded-2xl">
                      <CardContent className="p-4 flex flex-col items-center justify-center">
                        <Typography className="text-xs text-white/80 font-medium">Overall Trend</Typography>
                        <div className="flex items-center mt-1">
                          <span
                            className={`text-xl font-bold ${timelineData.overallTrend >= 0 ? "text-green-400" : "text-red-400"
                              }`}
                          >
                            {timelineData.overallTrend >= 0
                              ? `+${timelineData.overallTrend}`
                              : timelineData.overallTrend}{" "}
                            pts
                          </span>
                          {timelineData.overallTrend >= 0 ? (
                            <TrendingUp className="w-4 h-4 text-green-400 ml-1" />
                          ) : (
                            <TrendingDown className="w-4 h-4 text-red-400 ml-1" />
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                )}
              </div>

              {timelineLoading ? (
                <div className="flex justify-center py-20">
                  <CircularProgress sx={{ color: "#a855f7" }} />
                </div>
              ) : timelineData?.activities?.length > 0 ? (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Timeline path & progressions */}
                  <div className="lg:col-span-2 space-y-6">
                    {/* Nodes map */}
                    <div className="bg-[#1C1A26] border border-[#313131] rounded-3xl p-6">
                      <h3 className="text-sm font-semibold mb-6 flex items-center text-white/90">
                        <Briefcase className="w-4.5 h-4.5 text-purple-400 mr-2" />
                        Activity Timeline - {timelineData.accountName}
                      </h3>

                      <div className="relative flex items-center justify-between py-6 overflow-x-auto gap-4">
                        <div className="absolute top-1/2 left-0 right-0 h-0.5 bg-[#313131] -translate-y-1/2 z-0" />

                        {timelineData.activities.map((act: any, idx: number) => {
                          const isSelected = act.id === selectedActivityId;
                          const isRolePlay = act.type === "role_play";

                          return (
                            <button
                              key={act.id}
                              onClick={() => setSelectedActivityId(act.id)}
                              className="relative z-10 flex flex-col items-center focus:outline-none min-w-[100px] cursor-pointer"
                            >
                              <div
                                className={`w-12 h-12 rounded-full flex items-center justify-center border-2 transition-all duration-300 ${isSelected
                                    ? "bg-purple-600 border-purple-400 shadow-[0_0_15px_rgba(168,85,247,0.5)] scale-110"
                                    : act.evaluationStatus === "MANUAL_EVALUATION_REQUIRED" ||
                                      act.evaluationStatus === "PENDING_EVALUATION"
                                      ? "bg-[#1A1824] border-[#313131] opacity-60 hover:opacity-100"
                                      : "bg-[#1A1824] border-[#313131] hover:border-purple-500"
                                  }`}
                              >
                                {isRolePlay ? (
                                  <span className="text-xs font-semibold text-purple-300">🎭</span>
                                ) : (
                                  <span className="text-xs font-semibold text-blue-300">📞</span>
                                )}
                              </div>

                              <div className="mt-3 text-center">
                                <p className="text-xs font-bold text-white/90">
                                  {act.evaluationStatus === "MANUAL_EVALUATION_REQUIRED"
                                    ? "-"
                                    : act.evaluationStatus === "PENDING_EVALUATION"
                                      ? "..."
                                      : act.overallScore}
                                </p>
                                <p className="text-[10px] text-white/70 mt-1">
                                  {isRolePlay
                                    ? `Prep #${act.sequenceNumber}`
                                    : `Call #${act.sequenceNumber}`}
                                </p>
                                <p className="text-[9px] text-white/60">{formatDate(act.date)}</p>
                                {idx > 0 && act.evaluationStatus !== "PENDING_EVALUATION" && act.evaluationStatus !== "MANUAL_EVALUATION_REQUIRED" && (
                                  <p
                                    className={`text-[9px] font-bold mt-1 ${act.scoreDelta >= 0 ? "text-green-400" : "text-red-400"
                                      }`}
                                  >
                                    {act.scoreDelta >= 0 ? `+${act.scoreDelta}` : act.scoreDelta}{" "}
                                    {act.scoreDelta >= 0 ? "↑" : "↓"}
                                  </p>
                                )}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Chart */}
                    <div className="bg-[#1C1A26] border border-[#313131] rounded-3xl p-6">
                      <h3 className="text-sm font-semibold mb-6 flex items-center text-white/90">
                        <TrendingUp className="w-4.5 h-4.5 text-purple-400 mr-2" />
                        Score Progression Over Time
                      </h3>
                      <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart
                            data={getLineData()}
                            margin={{ top: 10, right: 10, left: -25, bottom: 0 }}
                          >
                            <CartesianGrid strokeDasharray="3 3" stroke="#313131" />
                            <XAxis dataKey="name" stroke="rgba(255, 255, 255, 0.7)" fontSize={11} />
                            <YAxis domain={[40, 100]} stroke="rgba(255, 255, 255, 0.7)" fontSize={11} />
                            <ChartTooltip
                              contentStyle={{
                                backgroundColor: "#1C1A26",
                                borderColor: "#313131",
                                borderRadius: "12px",
                              }}
                            />
                            <Line
                              type="monotone"
                              dataKey="Score"
                              stroke="#a855f7"
                              strokeWidth={3}
                              activeDot={{ r: 7, stroke: "#d8b4fe", strokeWidth: 2 }}
                            />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  </div>

                  {/* Sidebar detail and radar */}
                  <div className="space-y-6">
                    {selectedActivity && (
                      <div className="bg-[#1C1A26] border border-[#313131] rounded-3xl p-6 space-y-6">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-xl bg-purple-900/30 flex items-center justify-center text-purple-400">
                              {selectedActivity.type === "role_play" ? "🎭" : "📞"}
                            </div>
                            <div>
                              <h4 className="text-xs font-bold capitalize text-white/90">
                                {selectedActivity.type === "role_play" ? "Prep" : "Call"} #
                                {selectedActivity.sequenceNumber}
                              </h4>
                              <div className="flex items-center gap-2 text-[10px] text-white/70 mt-1">
                                <span className="flex items-center gap-1">
                                  <Calendar className="w-3 h-3" />
                                  {formatDate(selectedActivity.date)}
                                </span>
                                <span className="flex items-center gap-1">
                                  <Clock className="w-3 h-3" />
                                  {selectedActivity.durationMinutes} min
                                </span>
                              </div>
                            </div>
                          </div>

                          <div className="text-right">
                            {selectedActivity.evaluationStatus === "MANUAL_EVALUATION_REQUIRED" ? (
                              <button
                                onClick={handleEvaluate}
                                disabled={isEvaluating}
                                className="bg-purple-600 hover:bg-purple-700 text-white font-bold py-1.5 px-3 rounded-xl disabled:opacity-50 text-xs"
                              >
                                {isEvaluating ? "Analyzing..." : "Evaluate"}
                              </button>
                            ) : selectedActivity.evaluationStatus === "PENDING_EVALUATION" ? (
                              <div className="text-xs text-yellow-400 font-bold animate-pulse">
                                Evaluating...
                              </div>
                            ) : (
                              <>
                                <div className="text-2xl font-extrabold text-green-400">
                                  {selectedActivity.overallScore}
                                </div>
                                <div className="text-[9px] text-white/60">/100</div>
                                {selectedActivity.scoreDelta !== 0 && (
                                  <div
                                    className={`text-[10px] font-bold mt-0.5 ${selectedActivity.scoreDelta >= 0 ? "text-green-400" : "text-red-400"
                                      }`}
                                  >
                                    {selectedActivity.scoreDelta >= 0
                                      ? `+${selectedActivity.scoreDelta}`
                                      : selectedActivity.scoreDelta}
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        </div>

                        {selectedActivity.evaluationStatus !== "MANUAL_EVALUATION_REQUIRED" &&
                          selectedActivity.evaluationStatus !== "PENDING_EVALUATION" && (
                            <>
                              <div className="space-y-4">
                                {dimensionKeys.map((dim) => {
                                  const score = selectedActivity.scores?.[dim.key] || 0;
                                  const allScores = dimensionKeys.map(
                                    (d) => selectedActivity.scores?.[d.key] || 0
                                  );
                                  const minScore = Math.min(...allScores);
                                  const isLowest = score === minScore && allScores.length > 0;

                                  return (
                                    <div key={dim.key} className="space-y-1">
                                      <div className="flex items-center justify-between text-[11px]">
                                        <span className="text-white/80 font-medium">{dim.name}</span>
                                        <span
                                          className={`font-bold ${isLowest ? "text-blue-400" : "text-green-400"}`}
                                        >
                                          {score}
                                        </span>
                                      </div>
                                      <div className="h-1.5 w-full bg-[#1A1824] rounded-full overflow-hidden">
                                        <div
                                          className={`h-full rounded-full transition-all duration-500 ${isLowest ? "bg-blue-500" : "bg-green-500"
                                            }`}
                                          style={{ width: `${score}%` }}
                                        />
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>

                              {(selectedActivity.notableImprovement?.length > 0 ||
                                selectedActivity.notableDecline?.length > 0) && (
                                  <div className="border-t border-[#313131] pt-4 space-y-3 text-[11px]">
                                    {selectedActivity.notableImprovement?.length > 0 && (
                                      <div className="flex items-start gap-2">
                                        <span className="text-green-400 font-bold">↑</span>
                                        <div>
                                          <span className="text-white/80">Better: </span>
                                          <span className="text-green-400 font-medium">
                                            {selectedActivity.notableImprovement.join(", ")}
                                          </span>
                                        </div>
                                      </div>
                                    )}
                                    {selectedActivity.notableDecline?.length > 0 && (
                                      <div className="flex items-start gap-2">
                                        <span className="text-red-400 font-bold">↓</span>
                                        <div>
                                          <span className="text-white/80">Declined: </span>
                                          <span className="text-red-400 font-medium">
                                            {selectedActivity.notableDecline.join(", ")}
                                          </span>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                )}
                            </>
                          )}
                      </div>
                    )}

                    {/* Radar Profile */}
                    {timelineData?.activities?.length > 1 && (
                      <div className="bg-[#1C1A26] border border-[#313131] rounded-3xl p-6">
                        <h3 className="text-sm font-semibold mb-4 flex items-center text-white/90">
                          <Target className="w-4.5 h-4.5 text-purple-400 mr-2" />
                          Radar Profile (First vs Recent)
                        </h3>
                        <div className="h-60 flex justify-center">
                          <ResponsiveContainer width="100%" height="100%">
                            <RadarChart cx="50%" cy="50%" outerRadius="75%" data={getRadarData()}>
                              <PolarGrid stroke="#313131" />
                              <PolarAngleAxis
                                dataKey="subject"
                                stroke="rgba(255, 255, 255, 0.2)"
                                tick={{ fill: "#ffffff", fontSize: 10, fontWeight: 500 }}
                              />
                              <PolarRadiusAxis
                                angle={30}
                                domain={[0, 100]}
                                stroke="#313131"
                                fontSize={8}
                              />
                              <Radar
                                name="First Session"
                                dataKey="First"
                                stroke="#a855f7"
                                fill="#a855f7"
                                fillOpacity={0.15}
                              />
                              <Radar
                                name="Most Recent"
                                dataKey="Latest"
                                stroke="#3b82f6"
                                fill="#3b82f6"
                                fillOpacity={0.15}
                              />
                            </RadarChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="bg-[#1C1A26] border border-[#313131] rounded-3xl p-12 text-center text-white/90">
                  <Award className="w-10 h-10 text-white/80 mx-auto mb-4" />
                  <p className="text-sm font-semibold">No timelines available</p>
                  <p className="text-xs text-white/70 mt-1">
                    Select another account or sync practice sessions to verify deal data timelines.
                  </p>
                </div>
              )}
            </Box>
          )}

          {/* VIEW 0.1: REP PROGRESS DASHBOARD */}
          {timelineTab === 1 && (
            <Box className="space-y-6">
              <div className="flex justify-end">
                <FormControl variant="outlined" size="small" sx={{ minWidth: 150 }}>
                  <Select
                    value={repPeriod}
                    onChange={(e) => setRepPeriod(e.target.value as string)}
                    sx={{
                      color: "var(--color-text)",
                      ".MuiOutlinedInput-notchedOutline": { borderColor: "var(--color-border)" },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#a855f7" },
                      backgroundColor: "var(--color-bg)",
                      ".MuiSelect-icon": { color: "var(--color-text)" },
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          backgroundColor: "var(--color-surface)",
                          color: "var(--color-text)",
                          border: "1px solid var(--color-border)",
                        }
                      }
                    }}
                  >
                    <MenuItem value="30d">Last 30 days</MenuItem>
                    <MenuItem value="90d">Last 90 days</MenuItem>
                    <MenuItem value="all_time">All time</MenuItem>
                  </Select>
                </FormControl>
              </div>

              {repLoading ? (
                <div className="flex justify-center py-20">
                  <CircularProgress sx={{ color: "#a855f7" }} />
                </div>
              ) : repData ? (
                <div className="space-y-6">
                  {/* Stats Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                    <Card sx={{ backgroundColor: "var(--color-surface)", height: "100%" }} className="border border-[var(--color-border)] text-[var(--color-text)] rounded-3xl">
                      <CardContent className="p-6">
                        <Typography className="text-xs text-[var(--color-muted)] font-medium uppercase">
                          Overall Growth
                        </Typography>
                        <div className="flex items-center mt-2">
                          <Typography className="text-2xl font-extrabold text-green-400">
                            {repData.overallTrend >= 0
                              ? `+${repData.overallTrend}`
                              : repData.overallTrend}{" "}
                            pts
                          </Typography>
                          {repData.overallTrend >= 0 ? (
                            <TrendingUp className="w-5 h-5 text-green-400 ml-2" />
                          ) : (
                            <TrendingDown className="w-5 h-5 text-red-400 ml-2" />
                          )}
                        </div>
                      </CardContent>
                    </Card>

                    <Card sx={{ backgroundColor: "var(--color-surface)", height: "100%" }} className="border border-[var(--color-border)] text-[var(--color-text)] rounded-3xl">
                      <CardContent className="p-6">
                        <Typography className="text-xs text-[var(--color-muted)] font-medium uppercase">
                          Role Plays Completed
                        </Typography>
                        <Typography className="text-2xl font-extrabold text-purple-400 mt-2">
                          {repData.totalRolePlays}
                        </Typography>
                      </CardContent>
                    </Card>

                    <Card sx={{ backgroundColor: "var(--color-surface)", height: "100%" }} className="border border-[var(--color-border)] text-[var(--color-text)] rounded-3xl">
                      <CardContent className="p-6">
                        <Typography className="text-xs text-[var(--color-muted)] font-medium uppercase">
                          Meetings Completed
                        </Typography>
                        <Typography className="text-2xl font-extrabold text-blue-400 mt-2">
                          {repData.totalActualMeetings}
                        </Typography>
                      </CardContent>
                    </Card>

                    <Card sx={{ backgroundColor: "var(--color-surface)", height: "100%" }} className="border border-[var(--color-border)] text-[var(--color-text)] rounded-3xl">
                      <CardContent className="p-6">
                        <Typography className="text-xs text-[var(--color-muted)] font-medium uppercase">
                          Win Rate
                        </Typography>
                        <Typography className="text-2xl font-extrabold text-green-400 mt-2">
                          {repData.winRate}%
                        </Typography>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Dim progression & Practice ROI */}
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <div className="lg:col-span-2 bg-[var(--color-surface)] border border-[var(--color-border)] rounded-3xl p-6">
                      <h3 className="text-sm font-semibold mb-6 flex items-center text-[var(--color-text)]">
                        <Award className="w-4.5 h-4.5 text-purple-400 mr-2" />
                        Average Scores by Sales Dimension
                      </h3>
                      <div className="space-y-4">
                        {repData.dimensionProgress?.map((dim: any) => {
                          const hasImproved = dim.delta > 0;
                          return (
                            <div
                              key={dim.name}
                              className="flex items-center justify-between p-3.5 bg-[var(--color-bg)] rounded-2xl border border-[var(--color-border)]"
                            >
                              <div className="flex-1">
                                <div className="flex items-center justify-between text-xs font-semibold mb-2">
                                  <span className="text-[var(--color-text)]">{dim.name}</span>
                                  <div className="flex items-center gap-2">
                                    <span className="text-[var(--color-muted)]">{dim.score}/100</span>
                                    <span
                                      className={`flex items-center font-bold ${hasImproved ? "text-green-400" : "text-red-400"
                                        }`}
                                    >
                                      {dim.delta >= 0 ? `+${dim.delta}` : dim.delta}
                                      {hasImproved ? (
                                        <TrendingUp className="w-3 h-3 ml-0.5" />
                                      ) : (
                                        <TrendingDown className="w-3 h-3 ml-0.5" />
                                      )}
                                    </span>
                                  </div>
                                </div>
                                <div className="h-1.5 w-full bg-[var(--color-surface)] rounded-full overflow-hidden">
                                  <div
                                    className={`h-full rounded-full transition-all duration-500 ${dim.delta < -3 ? "bg-blue-500" : "bg-purple-500"
                                      }`}
                                    style={{ width: `${dim.score}%` }}
                                  />
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    <div className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-3xl p-6 flex flex-col justify-between">
                      <div>
                        <h3 className="text-sm font-semibold mb-2 flex items-center text-[var(--color-text)]">
                          <BookOpen className="w-4.5 h-4.5 text-purple-400 mr-2" />
                          Practice ROI
                        </h3>
                        <p className="text-xs text-[var(--color-muted)] mb-6">
                          Correlate session preparation with actual sales call performance.
                        </p>

                        <div className="space-y-4">
                          <div className="flex items-center justify-between p-4 bg-[var(--color-bg)] rounded-2xl border border-[var(--color-border)]">
                            <span className="text-xs text-[var(--color-muted)]">0 role plays avg</span>
                            <span className="text-base font-bold text-red-400">
                              {repData.practiceROI?.avgScoreAfter0Practice || 0}/100
                            </span>
                          </div>

                          <div className="flex items-center justify-between p-4 bg-[var(--color-bg)] rounded-2xl border border-[var(--color-border)]">
                            <span className="text-xs text-[var(--color-muted)]">1 role play avg</span>
                            <span className="text-base font-bold text-blue-400">
                              {repData.practiceROI?.avgScoreAfter1Practice || 0}/100
                            </span>
                          </div>

                          <div className="flex items-center justify-between p-4 bg-[var(--color-bg)] rounded-2xl border border-[var(--color-border)]">
                            <span className="text-xs text-[var(--color-muted)]">2+ role plays avg</span>
                            <span className="text-base font-bold text-green-400">
                              {repData.practiceROI?.avgScoreAfter2PlusPractice || 0}/100
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="py-12 text-center text-[var(--color-muted)] border border-dashed border-[var(--color-border)] rounded-2xl bg-[var(--color-surface)]">
                  No representative performance records found.
                </div>
              )}
            </Box>
          )}

          {/* VIEW 0.2: TEAM PROGRESS DASHBOARD */}
          {timelineTab === 2 && isManagerOrAdmin && (
            <Box className="space-y-6">
              <div className="flex justify-end">
                <FormControl variant="outlined" size="small" sx={{ minWidth: 150 }}>
                  <Select
                    value={teamPeriod}
                    onChange={(e) => setTeamPeriod(e.target.value as string)}
                    sx={{
                      color: "var(--color-text)",
                      ".MuiOutlinedInput-notchedOutline": { borderColor: "var(--color-border)" },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#a855f7" },
                      backgroundColor: "var(--color-bg)",
                      ".MuiSelect-icon": { color: "var(--color-text)" },
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          backgroundColor: "var(--color-surface)",
                          color: "var(--color-text)",
                          border: "1px solid var(--color-border)",
                        }
                      }
                    }}
                  >
                    <MenuItem value="30d">Last 30 days</MenuItem>
                    <MenuItem value="90d">Last 90 days</MenuItem>
                    <MenuItem value="all_time">All time</MenuItem>
                  </Select>
                </FormControl>
              </div>

              {teamLoading ? (
                <div className="flex justify-center py-20">
                  <CircularProgress sx={{ color: "#a855f7" }} />
                </div>
              ) : teamData?.length > 0 ? (
                <TableContainer sx={{ backgroundColor: "var(--color-surface)" }} className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)]">
                  <Table className="w-full text-sm text-left">
                    <TableHead sx={{ backgroundColor: "var(--color-surface)" }}>
                      <TableRow>
                        <TableCell sx={{ backgroundColor: "var(--color-surface)", color: "var(--color-text)", fontWeight: "bold", borderBottom: "1px solid var(--color-border)" }}>Rep Name</TableCell>
                        <TableCell sx={{ backgroundColor: "var(--color-surface)", color: "var(--color-text)", fontWeight: "bold", textAlign: "center", borderBottom: "1px solid var(--color-border)" }}>Prep Count</TableCell>
                        <TableCell sx={{ backgroundColor: "var(--color-surface)", color: "var(--color-text)", fontWeight: "bold", textAlign: "center", borderBottom: "1px solid var(--color-border)" }}>Meeting Count</TableCell>
                        <TableCell sx={{ backgroundColor: "var(--color-surface)", color: "var(--color-text)", fontWeight: "bold", textAlign: "center", borderBottom: "1px solid var(--color-border)" }}>Average Score</TableCell>
                        <TableCell sx={{ backgroundColor: "var(--color-surface)", color: "var(--color-text)", fontWeight: "bold", textAlign: "center", borderBottom: "1px solid var(--color-border)" }}>Growth Rate</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {teamData.map((row) => (
                        <TableRow
                          key={row.repId}
                          sx={{ borderTop: "1px solid var(--color-border)", "&:hover": { backgroundColor: "var(--color-divider)" } }}
                          className="transition"
                        >
                          <TableCell className="!text-white font-semibold">{row.repName}</TableCell>
                          <TableCell align="center" className="!text-white">{row.totalRolePlays}</TableCell>
                          <TableCell align="center" className="!text-white">{row.totalMeetings}</TableCell>
                          <TableCell align="center" className="!text-white font-bold text-purple-400">
                            {row.averageScore}/100
                          </TableCell>
                          <TableCell align="center">
                            <span
                              className={`font-bold ${row.trend >= 0 ? "text-green-400" : "text-red-400"
                                }`}
                            >
                              {row.trend >= 0 ? `+${row.trend}` : row.trend} pts
                            </span>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <div className="py-12 text-center text-[var(--color-muted)] border border-dashed border-[var(--color-border)] rounded-2xl bg-[var(--color-surface)]">
                  No team performance logs available.
                </div>
              )}
            </Box>
          )}
        </Box>
      )}

      {/* TAB 1: CRM SETUP & SYNC */}
      {activeOriginalIndex === 1 && (
        <div className="w-full flex flex-col gap-6">
          {/* Connection Status Banner */}
          <div
            className={`flex flex-col sm:flex-row items-start sm:items-center justify-between p-5 rounded-2xl border transition-all duration-300 gap-4 ${connected
                ? "bg-emerald-500/10 border-emerald-500/30 shadow-[0_0_25px_rgba(16,185,129,0.1)]"
                : "bg-rose-500/10 border-rose-500/30 shadow-[0_0_25px_rgba(244,63,94,0.1)]"
              }`}
          >
            <div className="flex items-center gap-3">
              {connected ? (
                <div className="relative flex items-center justify-center">
                  <span className="absolute inline-flex h-6 w-6 rounded-full bg-emerald-400 opacity-30 animate-ping"></span>
                  <CheckCircle className="w-8 h-8 text-emerald-400 relative" />
                </div>
              ) : (
                <AlertCircle className="w-8 h-8 text-rose-400" />
              )}
              <div>
                <h2 className="text-base font-bold text-white/90">
                  {connected ? "HubSpot Linked & Synced" : "HubSpot Disconnected"}
                </h2>
                <p className="text-xs text-white/70 leading-relaxed mt-0.5">
                  {connected
                    ? "Integration is active. Contact records, pitches, and meeting logs are synced in real-time."
                    : "Please configure credentials and authenticate using OAuth to start synchronization."}
                </p>
              </div>
            </div>

            {connected && (
              <button
                onClick={handleImportCRMData}
                disabled={syncing}
                className="w-full sm:w-auto bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 disabled:opacity-60 text-white rounded-xl py-2 px-4 font-semibold text-xs shadow-md active:scale-95 transition-all flex items-center justify-center gap-2"
              >
                {syncing ? (
                  <CircularProgress size={14} color="inherit" />
                ) : (
                  <RefreshCw className="w-3.5 h-3.5" />
                )}
                {syncing ? "Syncing..." : "Sync & Import Data"}
              </button>
            )}
          </div>

          {/* Bulk Import Progress Bar */}
          {importJob &&
            (importJob.status === "running" ||
              importJob.status === "pending" ||
              importJob.status === "retrying") && (
              <div className="bg-gradient-to-r from-purple-900/20 to-indigo-900/20 border border-purple-500/30 rounded-2xl p-5 shadow-lg flex flex-col gap-3">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                  <div className="flex items-center gap-2.5">
                    <CircularProgress size={14} color="secondary" className="text-purple-400" />
                    <span className="font-bold text-xs text-white/90">
                      {importJob.status === "pending"
                        ? "Preparing Bulk Import..."
                        : "Importing HubSpot Workspace Data..."}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 justify-between sm:justify-end">
                    <span className="text-[10px] text-purple-300 font-semibold bg-purple-500/10 px-2.5 py-1 rounded-full border border-purple-500/20">
                      {importJob.processedContacts} / {importJob.totalContacts} Contacts Complete
                    </span>
                    <button
                      onClick={handleCancelImport}
                      className="px-2.5 py-1 bg-rose-500/20 hover:bg-rose-500/30 text-rose-300 hover:text-white rounded-lg text-[10px] font-semibold transition-colors border border-rose-500/20 active:scale-95"
                    >
                      Stop Sync
                    </button>
                  </div>
                </div>

                <LinearProgress
                  variant="determinate"
                  value={
                    importJob.totalContacts > 0
                      ? (importJob.processedContacts / importJob.totalContacts) * 100
                      : 0
                  }
                  className="rounded-full h-2 bg-purple-950/40"
                  sx={{
                    "& .MuiLinearProgress-bar": {
                      backgroundImage: "linear-gradient(90deg, #A855F7 0%, #D8B4FE 100%)",
                    },
                  }}
                />
                <p className="text-[10px] text-white/70 leading-relaxed">
                  💡 Contact and account records are accessible instantly. AI digital twin
                  synthesis & company profile enrichment runs asynchronously in the background.
                  Large imports may take a few minutes. You can safely navigate away.
                </p>
              </div>
            )}

          {/* Credentials Card */}
          <div
            className="rounded-3xl p-6 sm:p-8 flex flex-col gap-6 shadow-2xl transition-all duration-300"
            style={{
              background: colors.surface,
              border: `1px solid ${colors.border}`,
              color: colors.text,
            }}
          >
            <div className="flex items-center gap-3">
              <Database className="w-5.5 h-5.5 text-purple-500" />
              <div>
                <h3 className="text-base font-bold" style={{ color: colors.text }}>HubSpot Integration API Access</h3>
                <p className="text-xs mt-0.5" style={{ color: colors.textMuted }}>
                  Authorize connection and credentials synchronization.
                </p>
              </div>
            </div>

            {/* Help instructions Collapsible */}
            <div
              className="rounded-2xl overflow-hidden transition-all duration-300"
              style={{
                background: colors.surfaceAlt,
                border: `1px solid ${colors.border}`,
              }}
            >
              <button
                onClick={() => setInstructionsOpen(!instructionsOpen)}
                className="w-full flex items-center justify-between p-4 hover:bg-black/5 dark:hover:bg-white/5 text-left transition-colors cursor-pointer"
              >
                <div className="flex items-center gap-2">
                  <span className="text-[9px] bg-purple-500/20 text-purple-600 dark:text-purple-300 px-2 py-0.5 rounded-full font-bold">
                    GUIDE
                  </span>
                  <span className="text-xs font-semibold" style={{ color: colors.text }}>
                    HubSpot Developer App Setup Instructions
                  </span>
                </div>
                {instructionsOpen ? (
                  <ChevronUp className="w-4 h-4" style={{ color: colors.text }} />
                ) : (
                  <ChevronDown className="w-4 h-4" style={{ color: colors.text }} />
                )}
              </button>

              {instructionsOpen && (
                <div
                  className="p-5 flex flex-col gap-6 text-sm leading-relaxed"
                  style={{
                    background: isDark ? "rgba(0, 0, 0, 0.4)" : colors.surface,
                    borderTop: `1px solid ${colors.border}`,
                    color: colors.text,
                  }}
                >
                  {/* Step 1 */}
                  <div>
                    <p className="font-bold text-base mb-2" style={{ color: colors.text }}>
                      Step 1: Install & Authenticate the HubSpot CLI
                    </p>
                    <p className="mb-2" style={{ color: colors.text }}>
                      HubSpot now requires using their Developer CLI to create new Apps with OAuth capabilities.
                    </p>
                    <ul className="list-decimal ml-5 space-y-3" style={{ color: colors.text }}>
                      <li>
                        <strong>Install the CLI:</strong> Run <code className="px-1.5 py-0.5 rounded font-mono" style={{ background: colors.surfaceAlt, border: `1px solid ${colors.border}` }}>npm install -g @hubspot/cli</code> in your terminal.
                      </li>
                      <li>
                        <strong>Authenticate:</strong> Run <code className="px-1.5 py-0.5 rounded font-mono" style={{ background: colors.surfaceAlt, border: `1px solid ${colors.border}` }}>hs account auth</code>.
                        <div
                          className="mt-2 ml-2 border-l-2 border-purple-500/50 pl-3 py-1.5 rounded-r-md"
                          style={{ background: isDark ? "rgba(255, 255, 255, 0.05)" : "rgba(168, 85, 247, 0.08)", color: colors.text }}
                        >
                          <strong>How to get your Personal Access Key:</strong> Log into your HubSpot Developer Account &gt; click the <strong>Settings (gear icon)</strong> &gt; <strong>Integrations</strong> &gt; <strong>Personal Access Key</strong>.
                        </div>
                      </li>
                    </ul>
                  </div>

                  {/* Step 2 */}
                  <div>
                    <p className="font-bold text-base mb-2" style={{ color: colors.text }}>
                      Step 2: Scaffold the App Architecture
                    </p>
                    <ul className="list-decimal ml-5 space-y-3" style={{ color: colors.text }}>
                      <li>
                        <strong>Initialize the Project:</strong> Run <code className="px-1.5 py-0.5 rounded font-mono" style={{ background: colors.surfaceAlt, border: `1px solid ${colors.border}` }}>hs project create</code>.
                        <ul className="list-disc ml-5 mt-2 space-y-1" style={{ color: colors.text }}>
                          <li>Name your project (e.g. <code className="px-1.5 py-0.5 rounded font-mono" style={{ background: colors.surfaceAlt, border: `1px solid ${colors.border}` }}>pitchster-hubspot-ui</code>)</li>
                          <li>Select the <strong>App</strong> template (Platform version 2026.03+)</li>
                          <li>When asked about features, select <strong>Card</strong> and <strong>Webhook</strong></li>
                        </ul>
                      </li>
                    </ul>
                  </div>

                  {/* Step 3 */}
                  <div>
                    <p className="font-bold text-base mb-2" style={{ color: colors.text }}>
                      Step 3: Configure the App Settings
                    </p>
                    <p className="mb-2" style={{ color: colors.text }}>
                      In modern HubSpot projects (2026.03+), configurations are decentralized into <code className="px-1.5 py-0.5 rounded font-mono" style={{ background: colors.surfaceAlt, border: `1px solid ${colors.border}` }}>*-hsmeta.json</code> files.
                    </p>
                    <ul className="list-decimal ml-5 space-y-4" style={{ color: colors.text }}>
                      <li>
                        Navigate into your newly created folder (e.g., <code className="px-1.5 py-0.5 rounded font-mono" style={{ background: colors.surfaceAlt, border: `1px solid ${colors.border}` }}>cd pitchster-hubspot-ui</code>).
                      </li>
                      <li>
                        Open the <strong><code className="px-1.5 py-0.5 rounded font-mono" style={{ background: colors.surfaceAlt, border: `1px solid ${colors.border}` }}>src/app/app-hsmeta.json</code></strong> file in your code editor and add/update the following fields:

                        <div className="mt-3 ml-2 space-y-4">
                          <div>
                            <strong>1. Distribution Mode:</strong><br />
                            This prevents HubSpot from requiring you to sign the public Marketplace Acceptable Use Policy, which blocks installation if unsigned.
                            <code
                              className="block mt-1 p-2.5 rounded-md font-mono"
                              style={{
                                background: isDark ? "rgba(0,0,0,0.6)" : colors.surfaceAlt,
                                border: `1px solid ${colors.border}`,
                                color: isDark ? "#fbbf24" : "#d97706",
                              }}
                            >
                              "distribution": "private"
                            </code>
                          </div>

                          <div>
                            <strong>2. Redirect URLs:</strong><br />
                            This tells HubSpot exactly where to send the user after they authorize the app. Add this URL:
                            <code
                              className="block mt-1 p-2.5 rounded-md font-mono break-all"
                              style={{
                                background: isDark ? "rgba(0,0,0,0.6)" : colors.surfaceAlt,
                                border: `1px solid ${colors.border}`,
                                color: isDark ? "#fbbf24" : "#d97706",
                              }}
                            >
                              "redirectUrls": ["{`${(apiClient.defaults.baseURL || import.meta.env.VITE_API_URL || import.meta.env.VITE_API_BASE_URL || 'http://localhost:5001').replace(/\/+$/, '')}/v1/crm/oauth/callback`}"]
                            </code>
                          </div>

                          <div>
                            <strong>3. Scopes:</strong><br />
                            This defines what data the app is allowed to access. Add these exact scopes:
                            <code
                              className="block mt-1 p-2.5 rounded-md font-mono whitespace-pre-wrap break-all"
                              style={{
                                background: isDark ? "rgba(0,0,0,0.6)" : colors.surfaceAlt,
                                border: `1px solid ${colors.border}`,
                                color: isDark ? "#fbbf24" : "#d97706",
                              }}
                            >
                              "requiredScopes": [<br />
                              &nbsp;&nbsp;"crm.objects.contacts.read",<br />
                              &nbsp;&nbsp;"crm.objects.contacts.write",<br />
                              &nbsp;&nbsp;"crm.objects.companies.read",<br />
                              &nbsp;&nbsp;"crm.objects.companies.write",<br />
                              &nbsp;&nbsp;"crm.objects.deals.read",<br />
                              &nbsp;&nbsp;"crm.objects.deals.write",<br />
                              &nbsp;&nbsp;"oauth"<br />
                              ]
                            </code>
                          </div>
                        </div>
                      </li>
                    </ul>
                  </div>

                  {/* Step 4 */}
                  <div>
                    <p className="font-bold text-base mb-2" style={{ color: colors.text }}>
                      Step 4: Add the Pitchster Insights Card Code
                    </p>
                    <p className="mb-2" style={{ color: colors.text }}>
                      To show AI insights inside HubSpot, you must add the card code to your project before uploading. Your folder structure should look like this:
                    </p>
                    <div className="bg-black/40 border border-purple-500/30 p-3 rounded-xl mb-4 font-mono text-xs text-purple-300">
{`your-project-folder/
├── hsproject.json
└── src/
    └── app/
        ├── app-hsmeta.json
        └── cards/
            ├── pitchster-card-hsmeta.json   <-- Create this
            └── PitchsterCard.tsx            <-- Create this`}
                    </div>
                    <ul className="list-decimal ml-5 space-y-4" style={{ color: colors.text }}>
                      <li>
                        Create a new file at <strong><code className="px-1.5 py-0.5 rounded font-mono" style={{ background: colors.surfaceAlt, border: `1px solid ${colors.border}` }}>src/app/cards/pitchster-card-hsmeta.json</code></strong> and paste the following:
                        <details className="mt-2 p-2 rounded bg-black/40 border border-purple-500/30">
                          <summary className="text-xs font-bold text-purple-400 cursor-pointer flex items-center justify-between">
                            <span>Show pitchster-card-hsmeta.json</span>
                            <div className="flex gap-2">
                              <button onClick={(e) => { 
                                e.preventDefault(); 
                                const code = e.currentTarget.closest('details')?.querySelector('code')?.innerText || '';
                                navigator.clipboard.writeText(code); 
                                toast.success("Code copied!"); 
                              }} className="bg-black/50 hover:bg-black/80 text-white px-2 py-1 rounded border border-white/10 flex items-center gap-1"><Copy size={12}/> Copy</button>
                              <button onClick={(e) => { 
                                e.preventDefault(); 
                                const code = e.currentTarget.closest('details')?.querySelector('code')?.innerText || '';
                                const a = document.createElement("a"); 
                                a.href = URL.createObjectURL(new Blob([code], { type: "application/json" })); 
                                a.download = "pitchster-card-hsmeta.json"; 
                                a.click(); 
                              }} className="bg-purple-600/50 hover:bg-purple-600/80 text-white px-2 py-1 rounded border border-purple-500/30 flex items-center gap-1">⬇️ Download</button>
                            </div>
                          </summary>
                          <code className="block mt-2 text-xs text-[#d97706] whitespace-pre-wrap font-mono cursor-text">
{`{
  "uid": "pitchster_insights_card",
  "type": "card",
  "config": {
    "name": "Pitchster Insights",
    "location": "crm.record.sidebar",
    "entrypoint": "/app/cards/PitchsterCard.tsx",
    "objectTypes": [
      "contacts",
      "deals"
    ]
  }
}`}
                          </code>
                        </details>
                      </li>
                      <li>
                        Create another file at <strong><code className="px-1.5 py-0.5 rounded font-mono" style={{ background: colors.surfaceAlt, border: `1px solid ${colors.border}` }}>src/app/cards/PitchsterCard.tsx</code></strong> and paste the following React code:
                        <details className="mt-2 p-2 rounded bg-black/40 border border-purple-500/30">
                          <summary className="text-xs font-bold text-purple-400 cursor-pointer flex items-center justify-between">
                            <span>Show PitchsterCard.tsx</span>
                            <div className="flex gap-2">
                              <button onClick={(e) => { 
                                e.preventDefault(); 
                                const code = e.currentTarget.closest('details')?.querySelector('code')?.innerText || '';
                                navigator.clipboard.writeText(code); 
                                toast.success("Code copied!"); 
                              }} className="bg-black/50 hover:bg-black/80 text-white px-2 py-1 rounded border border-white/10 flex items-center gap-1"><Copy size={12}/> Copy</button>
                              <button onClick={(e) => { 
                                e.preventDefault(); 
                                const code = e.currentTarget.closest('details')?.querySelector('code')?.innerText || '';
                                const a = document.createElement("a"); 
                                a.href = URL.createObjectURL(new Blob([code], { type: "text/typescript" })); 
                                a.download = "PitchsterCard.tsx"; 
                                a.click(); 
                              }} className="bg-purple-600/50 hover:bg-purple-600/80 text-white px-2 py-1 rounded border border-purple-500/30 flex items-center gap-1">⬇️ Download</button>
                            </div>
                          </summary>
                          <div className="max-h-96 overflow-y-auto mt-2 p-2 bg-black/60 rounded">
                            <code className="block text-[10px] text-blue-300 whitespace-pre font-mono cursor-text">
{`import React, { useState, useEffect } from "react";
import {
  hubspot,
  Flex,
  ProgressBar,
  Text,
  Tag,
  Button,
  Divider,
  Alert,
  LoadingSpinner,
  CrmContext,
  ExtensionPointApiActions,
  Panel,
  PanelBody,
  PanelSection,
  PanelFooter
} from '@hubspot/ui-extensions';

interface CrmExtensionProps {
  context: CrmContext;
  actions: ExtensionPointApiActions<'crm.record.sidebar'>;
}

hubspot.extend<'crm.record.sidebar'>(({ context, actions }: any) => (
  <PitchsterCard context={context} actions={actions} />
));

const PitchsterCard = ({ context, actions }: any) => {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    // Dynamically grab the HubSpot record type and ID that this card is rendering on
    const objectId = context.crm?.objectId;
    const objectType = context.crm?.objectType || "CONTACT";

    // Call the Pitchster backend directly via HubSpot's proxy
    hubspot.fetch(\`\${(import.meta.env.VITE_API_URL || "https://api.pitchster.ai").replace(/\\/+$/, '')}/v1/crm/hubspot/insights/\${objectType}/\${objectId}\`)
      .then(res => res.json())
      .then(response => {
        if (response.error) {
          setError(response.error);
        } else {
          setData(response);
        }
      })
      .catch((err: any) => {
        console.error(err);
        setError("Failed to fetch insights from Pitchster.");
      })
      .finally(() => setLoading(false));
  }, [context.crm]);

  if (loading) return <LoadingSpinner label="Loading Pitchster Insights..." />;
  if (error) return <Alert title="Error loading insights" variant="danger">{error}</Alert>;
  const hasData = data && (data.pitchId || (data.coachingInsights && data.coachingInsights.length > 0) || data.overallScore > 0);
  if (!hasData) return <Text>No Pitchster insights found for this record yet.</Text>;

  const pitchUrl = data.pitchId 
    ? \`https://app.pitchster.ai/pitch/\${data.pitchId}\`
    : "https://app.pitchster.ai";

  return (
    <Flex direction="column" gap="md">
      <Text format={{ fontWeight: "bold" }}>Overall Pitch Score</Text>
      <ProgressBar value={data.overallScore} showPercentage={true} />

      <Text format={{ fontWeight: "bold" }}>Discovery Score</Text>
      <ProgressBar value={data.discoveryScore} showPercentage={true} />

      <Text format={{ fontWeight: "bold" }}>Close Probability</Text>
      <ProgressBar value={data.closeProbability} showPercentage={true} />

      <Text format={{ fontWeight: "bold" }}>Objection Handling Score</Text>
      <ProgressBar value={data.objectionHandlingScore || 0} showPercentage={true} />

      <Divider />

      <Button
        variant="primary"
        overlay={
          <Panel id="pitchster-coaching-panel" title="Pitchster Coaching Report">
            <PanelBody>
              <PanelSection>
                <Flex direction="column" gap="md">
                  
                  {data.buyingSignals?.length > 0 && (
                    <>
                      <Text format={{ fontWeight: "bold" }}>💡 Buying Signals</Text>
                      <Flex direction="row" wrap="wrap" gap="small">
                        {data.buyingSignals.map((signal: string, i: number) => (
                          <Tag key={i} variant="success">{signal}</Tag>
                        ))}
                      </Flex>
                      <Divider />
                    </>
                  )}

                  {data.missedOpportunities?.length > 0 && (
                    <>
                      <Text format={{ fontWeight: "bold" }}>⚠️ Missed Opportunities</Text>
                      <Flex direction="column" gap="small">
                        {data.missedOpportunities.map((opp: string, i: number) => (
                          <Text key={i}>• {opp}</Text>
                        ))}
                      </Flex>
                      <Divider />
                    </>
                  )}

                  {data.recommendedActions?.length > 0 && (
                    <>
                      <Text format={{ fontWeight: "bold" }}>🎯 Recommended Actions</Text>
                      <Flex direction="column" gap="small">
                        {data.recommendedActions.map((action: string, i: number) => (
                          <Text key={i}>• {action}</Text>
                        ))}
                      </Flex>
                      <Divider />
                    </>
                  )}

                  {data.coachingInsights?.length > 0 && (
                    <>
                      <Text format={{ fontWeight: "bold" }}>🧠 Coaching Insights</Text>
                      <Flex direction="column" gap="small">
                        {data.coachingInsights.map((insight: string, i: number) => (
                          <Text key={i}>{insight}</Text>
                        ))}
                      </Flex>
                    </>
                  )}

                </Flex>
              </PanelSection>
            </PanelBody>
            <PanelFooter>
              <Button href={pitchUrl} external={true} variant="primary">
                View Full Pitch in Pitchster
              </Button>
            </PanelFooter>
          </Panel>
        }
      >
        View Full Coaching Report
      </Button>

      <Button href={pitchUrl} external={true} variant="secondary">
        Open in Pitchster
      </Button>
    </Flex>
  );
};

export default PitchsterCard;`}
                            </code>
                          </div>
                        </details>
                      </li>
                    </ul>
                  </div>

                  {/* Step 5 */}
                  <div className="mt-6">
                    <p className="font-bold text-base mb-2" style={{ color: colors.text }}>
                      Step 5: Upload and Finalize
                    </p>
                    <ul className="list-decimal ml-5 space-y-3" style={{ color: colors.text }}>
                      <li>
                        <strong>Upload to HubSpot:</strong> Run <code className="px-1.5 py-0.5 rounded font-mono" style={{ background: colors.surfaceAlt, border: `1px solid ${colors.border}` }}>hs project upload</code> in your terminal. This pushes your code and configuration directly to your Developer Account.
                      </li>
                      <li>
                        <strong>Retrieve your Credentials:</strong> Go to <strong>Projects</strong> in your HubSpot Developer Account dashboard in your browser. Click on your app, and navigate to the <strong>Auth</strong> tab.
                        Copy the <strong>Client ID</strong> and <strong>Client Secret</strong> and paste them in the form above.
                      </li>
                    </ul>
                    <div
                      className="mt-4 p-3 rounded-lg"
                      style={{
                        background: isDark ? "rgba(168, 85, 247, 0.2)" : "rgba(168, 85, 247, 0.08)",
                        border: `1px solid ${isDark ? "rgba(168, 85, 247, 0.4)" : "rgba(168, 85, 247, 0.2)"}`,
                        color: colors.text,
                      }}
                    >
                      <strong>Note on Legacy Apps:</strong> If you already have an app created before HubSpot forced the CLI usage, you can simply go to your <strong>Legacy Apps</strong>, click your app, and configure the Redirect URLs and Scopes directly in the UI under the Auth tab.
                    </div>
                  </div>

                  {/* Step 6 */}
                  <div className="mt-6">
                    <p className="font-bold text-base mb-2" style={{ color: colors.text }}>
                      Step 6: Webhook Settings & Real-Time Sync
                    </p>
                    <p className="mb-2" style={{ color: colors.text }}>
                      HubSpot uses your <strong>Client Secret</strong> to generate an <code className="px-1.5 py-0.5 rounded font-mono" style={{ background: colors.surfaceAlt, border: `1px solid ${colors.border}` }}>X-HubSpot-Signature</code> header. To receive real-time updates when deals or contacts change, add the following URL in the <strong>Webhooks</strong> tab of your app:
                    </p>
                    <div
                      className="flex items-center gap-2 p-2.5 rounded-xl font-mono mb-3"
                      style={{
                        background: isDark ? "rgba(0, 0, 0, 0.6)" : colors.surfaceAlt,
                        border: `1px solid ${colors.border}`,
                      }}
                    >
                      <code className="break-all flex-1 select-all text-sm" style={{ color: isDark ? "#60a5fa" : "#2563eb" }}>
                        {`${(apiClient.defaults.baseURL || import.meta.env.VITE_API_URL || import.meta.env.VITE_API_BASE_URL || 'http://localhost:5001').replace(/\/+$/, '')}/v1/crm/webhooks/hubspot`}
                      </code>
                      <button
                        onClick={() => {
                          const uri = `${(apiClient.defaults.baseURL || import.meta.env.VITE_API_URL || import.meta.env.VITE_API_BASE_URL || 'http://localhost:5001').replace(/\/+$/, '')}/v1/crm/webhooks/hubspot`;
                          navigator.clipboard.writeText(uri);
                          toast.success("Webhook URL copied!");
                        }}
                        className="p-1.5 hover:bg-black/10 dark:hover:bg-white/10 rounded-md transition-colors"
                        title="Copy Webhook URL"
                      >
                        <Copy size={13} style={{ color: colors.text }} />
                      </button>
                    </div>
                    <ul className="list-disc ml-5 space-y-1" style={{ color: colors.text }}>
                      <li>Enable subscriptions for <strong>Deal</strong> and <strong>Contact</strong> creation/updates.</li>
                    </ul>
                  </div>

                  {/* Pitchster-Card Step */}
                  <div className="mt-8 p-4 rounded-xl" style={{ background: "rgba(139, 92, 246, 0.1)", border: "1px solid rgba(139, 92, 246, 0.3)" }}>
                    <p className="font-bold text-base mb-2 text-purple-400">
                      Pitchster-Card: Revealing the Custom UI Extension
                    </p>
                    <p className="text-sm" style={{ color: "white" }}>
                      <strong>Important:</strong> HubSpot hides newly installed custom cards by default to protect existing layouts. Tell your customers to go to a Contact record in HubSpot, click <strong>⚙️ Customize</strong> (or <strong>Settings &gt; Record Customization</strong>), select <strong>Customize the right sidebar</strong>, and click <strong>Add Card</strong> to toggle on the <strong>Pitchster Insights</strong> visual gauge!
                    </p>
                  </div>

                </div>
              )}
            </div>

            {loading ? (
              <div className="flex justify-center py-8">
                <CircularProgress color="secondary" />
              </div>
            ) : (
              <div className="flex flex-col gap-5">
                {/* App Type Toggle */}
                <div className="flex flex-col gap-3 p-4 border border-[#313131] rounded-2xl bg-black/10">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-xs font-bold text-white/90">HubSpot App Type</h4>
                      <p className="text-[10px] text-white/70 mt-1">Select whether you are connecting via Pitchster's certified Marketplace App, or a custom Private App.</p>
                    </div>
                    <div className="flex items-center bg-[#1A1824] p-1 rounded-lg border border-[#313131]">
                      <button 
                        onClick={() => setAppType("PUBLIC")}
                        className={`px-3 py-1 text-[10px] font-bold rounded-md transition-all ${appType === "PUBLIC" ? "bg-purple-600 text-white shadow" : "text-white/60 hover:text-white"}`}
                      >
                        Marketplace (Public)
                      </button>
                      <button 
                        onClick={() => setAppType("PRIVATE")}
                        className={`px-3 py-1 text-[10px] font-bold rounded-md transition-all ${appType === "PRIVATE" ? "bg-purple-600 text-white shadow" : "text-white/60 hover:text-white"}`}
                      >
                        Custom (Private)
                      </button>
                    </div>
                  </div>

                  {appType === "PUBLIC" ? (
                    <div className="mt-3 bg-purple-900/20 border border-purple-500/30 p-3 rounded-xl">
                      <p className="text-xs text-purple-200">
                        Pitchster is a certified HubSpot Marketplace app. You do not need to provide API credentials. 
                        Simply click <b>Connect HubSpot OAuth</b> below to authorize your portal.
                      </p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
                      <div className="flex flex-col gap-1.5 w-full">
                        <label className="text-[10px] font-semibold tracking-wider uppercase" style={{ color: colors.textMuted }}>
                          Client ID
                        </label>
                        <input
                          type="text"
                          value={clientId}
                          onChange={(e) => setClientId(e.target.value)}
                          placeholder="e.g. 5e06a31c-xxxx-xxxx"
                          className="w-full rounded-xl px-4 py-2 text-xs focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500 transition-all font-mono"
                          style={{
                            background: colors.inputBg,
                            border: `1px solid ${colors.inputBorder}`,
                            color: colors.inputText,
                          }}
                        />
                      </div>
                      <div className="flex flex-col gap-1.5 w-full">
                        <label className="text-[10px] font-semibold tracking-wider uppercase" style={{ color: colors.textMuted }}>
                          Client Secret
                        </label>
                        <input
                          type="password"
                          value={clientSecret}
                          onChange={(e) => setClientSecret(e.target.value)}
                          placeholder="e.g. ••••••••••••"
                          className="w-full rounded-xl px-4 py-2 text-xs focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500 transition-all font-mono"
                          style={{
                            background: colors.inputBg,
                            border: `1px solid ${colors.inputBorder}`,
                            color: colors.inputText,
                          }}
                        />
                      </div>
                    </div>
                  )}
                </div>
                <div className="flex flex-col sm:flex-row gap-3.5 mt-2">
                  <button
                    onClick={handleSaveConfig}
                    className="w-full sm:w-1/2 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white font-semibold py-2.5 px-6 rounded-xl transition-all duration-300 shadow-[0_0_15px_rgba(168,85,247,0.3)] active:scale-95 text-center flex items-center justify-center gap-2 text-xs cursor-pointer"
                  >
                    Save Configuration
                  </button>
                  <button
                    onClick={handleTriggerOAuth}
                    className="w-full sm:w-1/2 border border-[#ff7a59] text-[#ff7a59] hover:bg-[#ff7a59]/10 font-semibold py-2.5 px-6 rounded-xl transition-all duration-300 active:scale-95 text-center flex items-center justify-center gap-2 text-xs cursor-pointer"
                  >
                    <Key className="w-3.5 h-3.5" />
                    Connect HubSpot OAuth
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Sync & Evaluation rules config */}
          <div className="bg-[#1C1A26] border border-[#313131] rounded-3xl p-6 sm:p-8 flex flex-col gap-6 shadow-2xl transition-all duration-300">
            <div className="flex items-center gap-3">
              <SettingsIcon className="w-5.5 h-5.5 text-purple-400" />
              <div>
                <h3 className="text-base font-bold text-white/90">Historical Data Evaluation</h3>
                <p className="text-xs text-white/70 mt-0.5">
                  Configure AI evaluation rules for imported calls and meetings.
                </p>
              </div>
            </div>

            <div className="flex flex-col gap-5">
              <div className="flex items-center justify-between p-4 bg-[#1A1824] border border-[#313131] rounded-2xl">
                <div>
                  <h4 className="text-xs font-bold text-white/90">
                    Evaluate Historical Engagements
                  </h4>
                  <p className="text-[10px] text-white/70 leading-relaxed mt-1">
                    When enabled, past calls and meetings will be scored by AI. Otherwise, only
                    new engagements sync.
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={evaluateHistorical}
                    onChange={(e) => setEvaluateHistorical(e.target.checked)}
                  />
                  <div className="w-11 h-6 bg-white/10 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-500"></div>
                </label>
              </div>

              {evaluateHistorical && (
                <div className="flex flex-col gap-1.5 w-full p-4 bg-[#1A1824] border border-[#313131] rounded-2xl transition-all">
                  <label className="text-[10px] font-semibold text-white/80 tracking-wider uppercase">
                    Lookback Period (Days)
                  </label>
                  <div className="flex items-center gap-3">
                    <input
                      type="number"
                      min="1"
                      max="365"
                      value={historicalDaysLimit}
                      onChange={(e) => setHistoricalDaysLimit(parseInt(e.target.value) || 30)}
                      className="w-24 bg-black/20 border border-[#313131] rounded-xl px-4 py-2 text-xs text-white focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500 transition-all font-mono"
                    />
                    <span className="text-[10px] text-white/70">
                      Maximum days in the past to evaluate engagements.
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Deal Linking controls */}
            <div className="flex items-center gap-3 mt-4 border-t border-[#313131] pt-6">
              <Briefcase className="w-5.5 h-5.5 text-purple-400" />
              <div>
                <h3 className="text-base font-bold text-white/90">Bidirectional Deal Linking</h3>
                <p className="text-xs text-white/70 mt-0.5">
                  Automatically link sales conversations to CRM deals based on AI insights.
                </p>
              </div>
            </div>

            <div className="flex flex-col gap-5">
              <div className="flex items-center justify-between p-4 bg-[#1A1824] border border-[#313131] rounded-2xl">
                <div>
                  <h4 className="text-xs font-bold text-white/90">Enable Deal Linking</h4>
                  <p className="text-[10px] text-white/70 leading-relaxed mt-1">
                    When enabled, evaluated calls and meetings will automatically be linked to
                    existing open deals if a match is detected.
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={enableDealLinking}
                    onChange={(e) => setEnableDealLinking(e.target.checked)}
                  />
                  <div className="w-11 h-6 bg-white/10 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-500"></div>
                </label>
              </div>

              {enableDealLinking && (
                <div className="flex items-center justify-between p-4 bg-[#1A1824] border border-[#313131] rounded-2xl transition-all">
                  <div>
                    <h4 className="text-xs font-bold text-white/90">Automatic Deal Creation</h4>
                    <p className="text-[10px] text-white/70 leading-relaxed mt-1">
                      When enabled, the AI will create a new CRM deal if it detects a new sales
                      opportunity that doesn't match existing deals.
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={enableDealCreation}
                      onChange={(e) => setEnableDealCreation(e.target.checked)}
                    />
                    <div className="w-11 h-6 bg-white/10 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-500"></div>
                  </label>
                </div>
              )}
            </div>
          </div>

          {/* Sync operations Audit logs */}
          <Box className="bg-[#1C1A26] border border-[#313131] rounded-2xl p-6 shadow-2xl">
            <Box className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
              <div className="flex items-center gap-2">
                <Activity className="w-5 h-5 text-purple-400" />
                <Typography variant="subtitle1" className="font-bold text-white/90">
                  Global Synchronization Audits
                </Typography>
              </div>
            </Box>

            {/* Search & Filter controls */}
            <Box className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <TextField
                variant="outlined"
                placeholder="Search by Tenant ID or Entity ID..."
                value={searchTenant}
                onChange={(e) => setSearchTenant(e.target.value)}
                InputProps={{
                  startAdornment: <Search className="w-4 h-4 text-white/70 mr-2" />,
                  style: { color: "#fff", backgroundColor: "rgba(255,255,255,0.03)", borderRadius: "12px" },
                }}
                className="border-[#313131] w-full"
              />

              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="bg-white/5 text-white border-[#313131]"
                sx={{ borderRadius: "12px", color: "white" }}
              >
                <MenuItem value="ALL">All Sync Statuses</MenuItem>
                <MenuItem value="SUCCESS">Success Only</MenuItem>
                <MenuItem value="FAILED">Failed Only</MenuItem>
              </Select>

              <Select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className="bg-white/5 text-white border-[#313131]"
                sx={{ borderRadius: "12px", color: "white" }}
              >
                <MenuItem value="ALL">All Entity Types</MenuItem>
                <MenuItem value="CONTACT">Contacts Syncs</MenuItem>
                <MenuItem value="PITCH">Pitches Syncs</MenuItem>
              </Select>
            </Box>

            <Divider className="my-4 border-[#313131]" />

            {/* Logs Table */}
            {loading ? (
              <div className="flex justify-center py-12">
                <CircularProgress color="secondary" />
              </div>
            ) : filteredLogs.length === 0 ? (
              <div className="py-12 text-center text-white/60 font-medium border border-dashed border-[#313131] rounded-2xl bg-white/5">
                No synchronization records match filters.
              </div>
            ) : (
              <TableContainer sx={{ backgroundColor: "#1A1824" }} className="rounded-xl border border-[#313131] bg-[#1A1824] max-h-[50vh] overflow-y-auto">
                <Table stickyHeader className="w-full text-sm text-left">
                  <TableHead sx={{ backgroundColor: "#1C1A26" }}>
                    <TableRow>
                      <TableCell sx={{ backgroundColor: "#1C1A26", color: "rgba(255,255,255,0.8)", fontWeight: "bold", borderBottom: "1px solid rgba(255, 255, 255, 0.1)" }}>Tenant ID</TableCell>
                      <TableCell sx={{ backgroundColor: "#1C1A26", color: "rgba(255,255,255,0.8)", fontWeight: "bold", borderBottom: "1px solid rgba(255, 255, 255, 0.1)" }}>Entity Type</TableCell>
                      <TableCell sx={{ backgroundColor: "#1C1A26", color: "rgba(255,255,255,0.8)", fontWeight: "bold", borderBottom: "1px solid rgba(255, 255, 255, 0.1)" }}>Entity ID</TableCell>
                      <TableCell sx={{ backgroundColor: "#1C1A26", color: "rgba(255,255,255,0.8)", fontWeight: "bold", textAlign: "center", borderBottom: "1px solid rgba(255, 255, 255, 0.1)" }}>Status</TableCell>
                      <TableCell sx={{ backgroundColor: "#1C1A26", color: "rgba(255,255,255,0.8)", fontWeight: "bold", textAlign: "center", borderBottom: "1px solid rgba(255, 255, 255, 0.1)" }}>Attempts</TableCell>
                      <TableCell sx={{ backgroundColor: "#1C1A26", color: "rgba(255,255,255,0.8)", fontWeight: "bold", borderBottom: "1px solid rgba(255, 255, 255, 0.1)" }}>Diagnostics / Errors</TableCell>
                      <TableCell sx={{ backgroundColor: "#1C1A26", color: "rgba(255,255,255,0.8)", fontWeight: "bold", borderBottom: "1px solid rgba(255, 255, 255, 0.1)" }}>Timestamp</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredLogs.map((log) => (
                      <TableRow
                        key={log.id}
                        className="border-t border-[#313131] text-white hover:bg-white/5 transition"
                      >
                        <TableCell className="!text-white font-mono text-xs">{log.tenantId}</TableCell>
                        <TableCell className="!text-white font-semibold text-xs">
                          <span className="px-2 py-0.5 rounded bg-white/10 text-purple-300">
                            {log.entityType}
                          </span>
                        </TableCell>
                        <TableCell className="!text-white font-mono text-xs">{log.entityId}</TableCell>
                        <TableCell className="text-center">
                          <span
                            className={`px-2 py-0.5 rounded-full font-bold text-[9px] uppercase tracking-wider ${log.status === "SUCCESS"
                                ? "bg-green-500/10 text-green-400 border border-green-500/20"
                                : "bg-red-500/10 text-red-400 border border-red-500/20"
                              }`}
                          >
                            {log.status}
                          </span>
                        </TableCell>
                        <TableCell className="!text-white text-center font-bold text-xs">
                          {log.retryCount + 1}
                        </TableCell>
                        <TableCell className="!text-white text-xs max-w-xs truncate">
                          {log.error ? (
                            <span className="text-red-400 font-medium" title={log.error}>
                              {log.error}
                            </span>
                          ) : (
                            <span className="text-white/60">—</span>
                          )}
                        </TableCell>
                        <TableCell className="!text-white/70 text-xs">
                          {new Date(log.createdAt).toLocaleString()}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </div>
      )}

      {/* TAB 2: MICROSOFT 365 & CALENDAR INTEGRATION */}
      {activeOriginalIndex === 2 && (
        <MicrosoftCalendarIntegration />
      )}
    </Box>
  );
};

export default Settings;